import React, { useState, useEffect, useCallback } from 'react';
import { Plus, ChevronDown, ChevronRight, Pencil, Trash2, Upload, AlertTriangle, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button, Modal, DateInput, formatDateDisplay, EmptyState } from '../../common';
import ConfirmDelete from '../common/ConfirmDelete';
import DocumentManager from '../common/DocumentManager';
import UserSelect from '../common/UserSelect';
import { SearchableSelect } from '../../common/SearchableSelect';
import {
  getEvents, createEvent, updateEvent, deleteEvent, getContracts,
  addEventDocument, updateEventDocument, deleteEventDocument,
} from '../../../services/dossier.service';
import { getImplementationDocTypes, type ImplementationDocType, getImplementationTypes, type ImplementationType } from '../../../services/nomenclatoare.service';
import type { ImplementationEvent, DossierContract } from '../../../types';

interface Props {
  dossierId: number;
  projectId: number;
  summary: { totalEvents: number };
  onRefresh: () => void;
}

const EMPTY_EVENT_FORM = { event_type: '', event_number: '', event_date: '', expiry_date: '', title: '', notes: '', contract_id: '', responsible_user_id: null as number | null };
const toDateStr = (v: any) => v ? String(v).substring(0, 10) : '';

export default function ImplementareSection({ dossierId, projectId, summary, onRefresh }: Props) {
  const [events, setEvents] = useState<ImplementationEvent[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [eventForm, setEventForm] = useState({ ...EMPTY_EVENT_FORM });
  const [editingEventId, setEditingEventId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({ ...EMPTY_EVENT_FORM });
  const [deleteTarget, setDeleteTarget] = useState<ImplementationEvent | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [eventTypes, setEventTypes] = useState<ImplementationType[]>([]);
  const [contracts, setContracts] = useState<DossierContract[]>([]);
  const [docTypes, setDocTypes] = useState<ImplementationDocType[]>([]);
  const [addDocEventId, setAddDocEventId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadDocTypes = async () => {
    try {
      const data = await getImplementationDocTypes();
      setDocTypes(data.filter(d => d.is_active));
    } catch (err) { console.error(err); }
  };

  const loadData = useCallback(async () => {
    try {
      const e = await getEvents(dossierId);
      setEvents(e);
      setLoadError(false);
      if (e.length === 1 && expanded === null) setExpanded(e[0].id);
    } catch (err) {
      console.error(err);
      setLoadError(true);
      toast.error('Nu s-au putut încărca evenimentele de implementare.');
    }
    finally { setInitialLoading(false); }
  }, [dossierId]);

  const loadEventTypes = async () => {
    try {
      const data = await getImplementationTypes();
      setEventTypes(data.filter(t => t.is_active));
    } catch (err) { console.error(err); }
  };

  useEffect(() => { loadData(); loadDocTypes(); loadEventTypes(); }, [loadData]);
  useEffect(() => { getContracts(dossierId).then(setContracts).catch(console.error); }, [dossierId]);

  const cleanDates = (data: Record<string, any>) => {
    const cleaned = { ...data };
    if (cleaned.event_date === '') cleaned.event_date = null;
    if (cleaned.expiry_date === '') cleaned.expiry_date = null;
    cleaned.contract_id = cleaned.contract_id ? parseInt(cleaned.contract_id) : null;
    return cleaned;
  };

  const handleAddEvent = async () => {
    setCreating(true);
    try {
      await createEvent(dossierId, cleanDates({
        ...eventForm,
        description: eventForm.title,
      }));
      setShowEventModal(false);
      setEventForm({ ...EMPTY_EVENT_FORM });
      await loadData();
      onRefresh();
      toast.success('Eveniment adăugat.');
    } catch (err) {
      console.error('Create event error:', err);
      toast.error('Evenimentul nu a putut fi creat. Verifică datele și încearcă din nou.');
    }
    finally { setCreating(false); }
  };

  const handleDeleteEvent = async (ev: ImplementationEvent) => {
    try {
      await deleteEvent(dossierId, ev.id);
      if (expanded === ev.id) setExpanded(null);
      await loadData();
      onRefresh();
      toast.success('Eveniment șters.');
    } catch (err) {
      console.error('Delete event error:', err);
      toast.error('Evenimentul nu a putut fi șters. Încearcă din nou.');
    }
  };

  const handleStartEdit = (ev: ImplementationEvent) => {
    setEditingEventId(ev.id);
    setEditForm({
      event_type: ev.event_type,
      event_number: ev.event_number || '',
      event_date: toDateStr(ev.event_date),
      expiry_date: toDateStr(ev.expiry_date),
      title: ev.description || '',
      notes: ev.notes || '',
      contract_id: ev.contract_id ? String(ev.contract_id) : '',
      responsible_user_id: ev.responsible_user_id || null,
    });
  };

  const handleSaveEdit = async () => {
    if (editingEventId === null) return;
    setSaving(true);
    try {
      await updateEvent(dossierId, editingEventId, cleanDates({
        ...editForm,
        description: editForm.title,
      }));
      setEditingEventId(null);
      await loadData();
      onRefresh();
      toast.success('Modificările au fost salvate.');
    } catch (err) {
      console.error('Update event error:', err);
      toast.error('Modificările nu au putut fi salvate. Încearcă din nou.');
    }
    finally { setSaving(false); }
  };

  const inputClass = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500";

  const contractLabel = (c: DossierContract) => c.contract_number ? `${c.contract_number} - ${c.contract_name}` : c.contract_name;

  // Shared form fields renderer
  const renderEventFormFields = (form: typeof eventForm, setForm: (fn: (f: typeof eventForm) => typeof eventForm) => void) => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Contract asociat <span className="text-gray-400 font-normal">(recomandat)</span></label>
        <SearchableSelect
          value={form.contract_id}
          onChange={v => setForm(f => ({...f, contract_id: v as string}))}
          placeholder="— Fără contract —"
          options={contracts.map(c => ({ value: c.id, label: contractLabel(c) }))}
        />
        {!form.contract_id && (
          <p className="mt-1 text-xs text-amber-600">Recomandăm asocierea evenimentului cu un contract al dosarului.</p>
        )}
        {contracts.length === 0 && (
          <p className="mt-1 text-xs text-gray-400">Nu există contracte în acest dosar.</p>
        )}
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Tip eveniment</label>
        <SearchableSelect
          value={form.event_type}
          onChange={v => setForm(f => ({...f, event_type: v as string}))}
          placeholder="-- Selectează tipul --"
          options={eventTypes.map(t => ({ value: t.code, label: `${t.code} - ${t.name}` }))}
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Număr</label>
          <input placeholder="Număr" value={form.event_number} onChange={e => setForm(f => ({...f, event_number: e.target.value}))} className={inputClass} />
        </div>
        <DateInput label="Data" value={form.event_date} onChange={e => setForm(f => ({...f, event_date: e.target.value}))} />
      </div>
      <DateInput label="Data expirare (opțional)" value={form.expiry_date} onChange={e => setForm(f => ({...f, expiry_date: e.target.value}))} />
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Titlu</label>
        <input placeholder="Titlu" value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} className={inputClass} />
      </div>
      <UserSelect
        projectId={projectId}
        value={form.responsible_user_id}
        onChange={userId => setForm(f => ({...f, responsible_user_id: userId}))}
      />
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Observații</label>
        <textarea placeholder="Observații" value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} className={inputClass} rows={2} />
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-gray-600 font-medium">{summary.totalEvents} evenimente</div>
        <Button variant="primary" size="sm" leftIcon={<Plus className="w-4 h-4" />} onClick={() => setShowEventModal(true)}>Adaugă eveniment</Button>
      </div>

      {/* Add Event Modal */}
      <Modal
        isOpen={showEventModal}
        onClose={() => { setShowEventModal(false); setEventForm({ ...EMPTY_EVENT_FORM }); }}
        title="Eveniment nou"
        size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" size="md" onClick={() => { setShowEventModal(false); setEventForm({ ...EMPTY_EVENT_FORM }); }}>Anulează</Button>
            <Button variant="primary" size="md" onClick={handleAddEvent} disabled={creating} isLoading={creating}>
              {creating ? 'Se creează...' : 'Creează'}
            </Button>
          </div>
        }
      >
        {renderEventFormFields(eventForm, setEventForm)}
      </Modal>

      {/* Edit Event Modal */}
      <Modal
        isOpen={editingEventId !== null}
        onClose={() => setEditingEventId(null)}
        title="Editare eveniment"
        size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" size="md" onClick={() => setEditingEventId(null)}>Anulează</Button>
            <Button variant="primary" size="md" onClick={handleSaveEdit} disabled={saving} isLoading={saving}>
              {saving ? 'Se salvează...' : 'Salvează'}
            </Button>
          </div>
        }
      >
        {renderEventFormFields(editForm, setEditForm)}
      </Modal>

      {initialLoading ? (
        <div className="text-center py-8 text-sm text-gray-500">Se încarcă...</div>
      ) : loadError ? (
        <div className="text-center py-12">
          <div className="flex justify-center mb-4">
            <AlertTriangle className="w-12 h-12 text-red-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Nu s-au putut încărca evenimentele</h3>
          <p className="text-gray-500 mb-4 max-w-sm mx-auto">A apărut o eroare la încărcarea datelor. Verifică conexiunea și încearcă din nou.</p>
          <Button variant="secondary" leftIcon={<RefreshCw className="w-4 h-4" />} onClick={() => { setInitialLoading(true); loadData(); }}>
            Reîncearcă
          </Button>
        </div>
      ) : events.length === 0 ? (
        contracts.length === 0 ? (
          <EmptyState
            icon="calendar"
            title="Niciun eveniment de implementare"
            description="Îți recomandăm să adaugi întâi un contract în secțiunea Contracte, apoi să înregistrezi evenimentele de implementare asociate. Poți totuși adăuga un eveniment și fără contract."
            action={{ label: 'Adaugă eveniment', onClick: () => setShowEventModal(true) }}
          />
        ) : (
          <EmptyState
            icon="calendar"
            title="Niciun eveniment de implementare"
            description="Înregistrează evenimentele de implementare ale dosarului (rapoarte, procese-verbale, etc.) și asociază-le contractelor."
            action={{ label: 'Adaugă eveniment', onClick: () => setShowEventModal(true) }}
          />
        )
      ) : (
        <div className="overflow-hidden border border-gray-200 rounded-lg"><div className="divide-y divide-gray-100">
          {events.map(ev => {
            const isExpanded = expanded === ev.id;
            const docs = ev.documents || [];
            const docCount = docs.length;

            return (
              <div key={ev.id} className="bg-white">
                <div className="flex items-center hover:bg-blue-50 transition-colors cursor-pointer group">
                  <button
                    onClick={() => setExpanded(isExpanded ? null : ev.id)}
                    className="flex-1 flex items-center gap-3 px-4 py-3 text-left min-w-0"
                  >
                    {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />}
                    {ev.event_type && (
                      <span className="text-xs font-mono text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded flex-shrink-0">{ev.event_type}</span>
                    )}
                    <span className="flex-1 text-sm font-medium text-gray-900 truncate">
                      {ev.description || (eventTypes.find(t => t.code === ev.event_type)?.name) || ev.event_type || 'Eveniment fără titlu'}
                      {ev.event_number ? ` nr. ${ev.event_number}` : ''}
                    </span>
                    {ev.contract_id ? (
                      <span className="text-xs text-gray-400 flex-shrink-0 truncate max-w-[180px]" title={ev.contract_name || undefined}>
                        {ev.contract_number || ev.contract_name}
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-100 flex-shrink-0" title="Eveniment neasociat unui contract">Fără contract</span>
                    )}
                    {ev.responsible_user_name && (
                      <span className="text-xs text-gray-400 flex-shrink-0">{ev.responsible_user_name}</span>
                    )}
                    {ev.event_date && <span className="text-xs text-gray-400 flex-shrink-0">{formatDateDisplay(toDateStr(ev.event_date))}</span>}
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">{docCount} doc</span>
                  </button>
                  <div className="flex items-center gap-1 pr-4">
                    <button onClick={(e) => { e.stopPropagation(); setAddDocEventId(addDocEventId === ev.id ? null : ev.id); }} className="p-1.5 rounded hover:bg-green-100 text-gray-400 hover:text-green-600 transition-colors" title="Adaugă document">
                      <Upload className="w-4 h-4" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); handleStartEdit(ev); }} className="p-1.5 text-gray-400 hover:text-blue-600 rounded hover:bg-blue-100 transition-colors" title="Editează">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); setDeleteTarget(ev); }} className="p-1.5 text-gray-400 hover:text-red-600 rounded hover:bg-red-100 transition-colors" title="Șterge">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {(addDocEventId === ev.id || isExpanded) && (
                  <div>
                    {(ev.description || ev.notes) && isExpanded && (
                      <div className="px-4 py-2 border-t border-gray-200 bg-gray-50 text-xs text-gray-500">
                        {ev.description && <span className="font-medium text-gray-600">{ev.description}</span>}
                        {ev.description && ev.notes && <span> - </span>}
                        {ev.notes && <span className="italic">{ev.notes}</span>}
                      </div>
                    )}
                    {isExpanded && (
                      <div className={`px-4 pt-3 flex justify-end ${(ev.description || ev.notes) ? 'bg-gray-50' : 'border-t border-gray-200 bg-gray-50'}`}>
                        <Button variant="secondary" size="sm" leftIcon={<Upload className="w-4 h-4" />} onClick={() => setAddDocEventId(ev.id)}>
                          Încarcă fișier
                        </Button>
                      </div>
                    )}
                    <DocumentManager
                      documents={docs}
                      docTypes={docTypes}
                      requireDocType={true}
                      showDateFields={false}
                      entityId={ev.id}
                      entityPrefix="impl"
                      projectId={projectId}
                      onAdd={(file, meta) => addEventDocument(ev.id, file, meta)}
                      onUpdate={(docId, data) => updateEventDocument(ev.id, docId, data)}
                      onDelete={async (docId) => { await deleteEventDocument(ev.id, docId); }}
                      onRefresh={() => { loadData(); onRefresh(); }}
                      isAddFormOpen={addDocEventId === ev.id}
                      onAddFormOpenChange={(open) => setAddDocEventId(open ? ev.id : null)}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div></div>
      )}

      <ConfirmDelete
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => { if (deleteTarget) handleDeleteEvent(deleteTarget); }}
        message="Ești sigur că vrei să ștergi acest eveniment? Toate documentele asociate vor fi șterse."
      />
    </div>
  );
}
